import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Truck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/products/ProductCard';
import { companyInfo, warrantyDisclaimer } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CatalogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  // Синхронизация категории с URL параметрами
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') || 'all';
    if (categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortBy, setSortBy] = useState('price-asc');
  const [inStockOnly, setInStockOnly] = useState(false);
  
  // Dynamic data from API
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/products`),
          fetch(`${API_URL}/api/categories`)
        ]);
        
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
        }
        
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const sizes = [
    { id: '3x4', label: '3x4 м' },
    { id: '3x6', label: '3x6 м' },
    { id: '3x8', label: '3x8 м' },
    { id: '3x10', label: '3x10 м' }
  ];

  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    // Price filter
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Size filter
    if (selectedSizes.length > 0) {
      result = result.filter(p => {
        const productSize = `${p.size.width}x${p.size.length}`;
        return selectedSizes.includes(productSize);
      });
    }

    // In stock filter
    if (inStockOnly) {
      result = result.filter(p => p.inStock);
    }

    // Sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        result.sort((a, b) => (b.isHit ? 1 : 0) - (a.isHit ? 1 : 0));
    }

    return result;
  }, [products, selectedCategory, searchQuery, priceRange, selectedSizes, sortBy, inStockOnly]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handleSizeToggle = (sizeId) => {
    setSelectedSizes(prev => 
      prev.includes(sizeId) 
        ? prev.filter(s => s !== sizeId)
        : [...prev, sizeId]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPriceRange([0, 200000]);
    setSelectedCategory('all');
    setSelectedSizes([]);
    setInStockOnly(false);
    searchParams.delete('category');
    setSearchParams(searchParams);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  const activeFiltersCount = (
    (selectedCategory !== 'all' ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < 200000 ? 1 : 0) +
    selectedSizes.length +
    (inStockOnly ? 1 : 0)
  );

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Загрузка каталога...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 py-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Каталог теплиц</h1>
          <p className="text-gray-600">Теплицы из поликарбоната с доставкой по {companyInfo.region}</p>
          <Link to="/delivery" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mt-2 text-sm">
            <Truck className="w-4 h-4" />
            Узнать о доставке в ваш город или район
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`lg:w-72 flex-shrink-0 ${
            showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-y-auto lg:static lg:p-0 lg:bg-transparent' : 'hidden lg:block'
          }`}>
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <h2 className="text-xl font-bold">Фильтры</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-6">
              {/* Search */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Поиск</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Название теплицы..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Categories */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Модель</Label>
                <div className="space-y-2">
                  {categories.filter(c => c.id !== 'custom').map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-emerald-100 text-emerald-800 font-medium'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Цена</Label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={200000}
                  step={1000}
                  className="mb-3"
                />
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{formatPrice(priceRange[0])} ₽</span>
                  <span>—</span>
                  <span>{formatPrice(priceRange[1])} ₽</span>
                </div>
              </div>

              {/* Size */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Размер</Label>
                <div className="grid grid-cols-2 gap-2">
                  {sizes.map(size => (
                    <label
                      key={size.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        selectedSizes.includes(size.id)
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Checkbox
                        checked={selectedSizes.includes(size.id)}
                        onCheckedChange={() => handleSizeToggle(size.id)}
                        className="hidden"
                      />
                      <span className="text-sm">{size.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* In stock */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={inStockOnly}
                    onCheckedChange={setInStockOnly}
                  />
                  <span className="text-sm text-gray-700">Только в наличии</span>
                </label>
              </div>

              {/* Clear filters */}
              {activeFiltersCount > 0 && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Сбросить фильтры ({activeFiltersCount})
                </Button>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  className="lg:hidden"
                  onClick={() => setShowFilters(true)}
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Фильтры
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 bg-emerald-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
                <span className="text-gray-600 text-sm">
                  Найдено: <strong>{filteredProducts.length}</strong> товаров
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 hidden sm:block">Сортировка:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="popular">По популярности</SelectItem>
                    <SelectItem value="price-asc">Цена: по возрастанию</SelectItem>
                    <SelectItem value="price-desc">Цена: по убыванию</SelectItem>
                    <SelectItem value="name">По названию</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products - Grouped by Category */}
            {selectedCategory === 'all' && filteredProducts.length > 0 ? (
              <div className="space-y-12">
                {/* Group products by category */}
                {categories.filter(c => c.id !== 'all' && c.id !== 'custom').map(category => {
                  const categoryProducts = filteredProducts.filter(p => p.category === category.id);
                  if (categoryProducts.length === 0) return null;
                  
                  return (
                    <div key={category.id}>
                      <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
                        <Link 
                          to={`/catalog?category=${category.id}`}
                          className="text-sm text-emerald-600 hover:text-emerald-700"
                        >
                          Все {category.name} →
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {categoryProducts.map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg mb-4">Товары не найдены</p>
                <Button variant="outline" onClick={clearFilters}>
                  Сбросить фильтры
                </Button>
              </div>
            )}

            {/* Disclaimers */}
            <div id="warranty-disclaimer" className="mt-12 pt-6 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-500">
                <sup>*</sup> {warrantyDisclaimer}
              </p>
              <p className="text-sm text-gray-500">
                Информация на сайте не является публичной офертой, определяемой положениями ст. 437 ГК РФ.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CatalogPage;
